import { ChevronRight } from 'lucide-react';
import { Link, useRouterState } from '@tanstack/react-router';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar';
import { NAV_SECTIONS, matchScore } from '@/components/nav-config';

// Top-level item: muted by default, solid-blue pill when active. Micro-motion:
// idle items nudge right on hover, the icon lifts slightly, and there's a small
// press feedback — all subtle and eased.
const ITEM =
  'h-9 gap-3 rounded-lg px-3 font-medium text-sidebar-foreground/70 transition-all duration-200 ease-out ' +
  'hover:bg-primary/5 hover:text-sidebar-foreground data-[active=false]:hover:translate-x-0.5 active:scale-[0.98] ' +
  '[&>svg:first-child]:transition-transform [&>svg:first-child]:duration-200 hover:[&>svg:first-child]:scale-110 ' +
  'data-[active=true]:bg-primary data-[active=true]:text-primary-foreground data-[active=true]:shadow-sm ' +
  'data-[active=true]:hover:bg-primary data-[active=true]:hover:text-primary-foreground ' +
  'group-data-[collapsible=icon]:mx-auto';

// Sub-item: quieter — accent text + a small hover nudge when active.
const SUB =
  'rounded-md text-sidebar-foreground/60 transition-all duration-200 ease-out ' +
  'hover:translate-x-0.5 hover:text-sidebar-foreground ' +
  'data-[active=true]:bg-transparent data-[active=true]:font-medium data-[active=true]:text-primary';

const LABEL =
  'px-3 text-[11px] font-medium uppercase tracking-wider text-sidebar-foreground/45';

export function NavMain() {
  const currentPath = useRouterState({ select: (s) => s.location.pathname });

  return (
    <>
      {NAV_SECTIONS.map((section) => (
        <SidebarGroup key={section.label} className="gap-1 group-data-[collapsible=icon]:p-0">
          <SidebarGroupLabel className={LABEL}>{section.label}</SidebarGroupLabel>
          <SidebarMenu className="gap-1">
            {section.items.map((item) => {
              if (!item.items) {
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      tooltip={item.title}
                      asChild
                      isActive={matchScore(item.url, currentPath) > 0}
                      className={ITEM}
                    >
                      <Link to={item.url}>
                        <item.icon />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              }

              // Light up the single best-matching child, if any.
              let activeChild = -1;
              let best = 0;
              item.items.forEach((sub, i) => {
                const score = matchScore(sub.url, currentPath);
                if (score > best) {
                  best = score;
                  activeChild = i;
                }
              });
              const groupActive = activeChild !== -1;

              return (
                <Collapsible
                  key={item.title}
                  asChild
                  defaultOpen={groupActive}
                  className="group/collapsible"
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton
                        tooltip={item.title}
                        isActive={groupActive}
                        className={ITEM}
                      >
                        <item.icon />
                        <span>{item.title}</span>
                        <ChevronRight className="ml-auto size-4! transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-[sidebar-collapsible-up_200ms_ease-out] data-[state=open]:animate-[sidebar-collapsible-down_200ms_ease-out]">
                      <SidebarMenuSub className="gap-1">
                        {item.items.map((subItem, i) => (
                          <SidebarMenuSubItem key={subItem.title}>
                            <SidebarMenuSubButton
                              asChild
                              isActive={i === activeChild}
                              className={SUB}
                            >
                              <Link to={subItem.url}>
                                <span>{subItem.title}</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      ))}
    </>
  );
}
